import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";

import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    IconButton,
    InputAdornment
} from "@mui/material";

import { Visibility, VisibilityOff, LockOutlined, EmailOutlined } from "@mui/icons-material";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
    });

    // System details
    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
    }, []);

    // Auto login if stored
    useEffect(() => {
        const savedEmail = localStorage.getItem("email");
        const savedPassword = localStorage.getItem("password");
        if (savedEmail && savedPassword) {
            setEmail(savedEmail);
            setPassword(savedPassword);
            login(savedEmail, savedPassword, true);
        }
    }, []);

    const login = async (emailParam, passwordParam, auto = false) => {
        const userEmail = auto ? emailParam : email;
        const userPassword = auto ? passwordParam : password;

        if (!userEmail || !userPassword) {
            Toast.fire({ icon: "warning", title: "Please fill in all fields" });
            return;
        }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, userEmail, userPassword);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "users", user.uid);
                const docSnap = await getDoc(docRef);

                    if (!auto) {
                        localStorage.setItem("email", userEmail);
                        localStorage.setItem("password", userPassword);
                    }

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name,
                        systemDetails,
                        createdTime: Timestamp.now()
                    });

                    Toast.fire({
                        icon: "success",
                        title: "Welcome Back!"
                    });

                    window.location.href = "/home";
               
            }
        } catch (error) {
            if (!auto) {
                Toast.fire({
                    icon: "error",
                    title: "Invalid Credentials"
                });
            }
            localStorage.removeItem("email");
            localStorage.removeItem("password");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        login();
    };

    return (
        <Box sx={{ height: "100vh", display: "flex", bgcolor: "#f8f9fa" }}>
            
            {/* LEFT SIDE: BRAND PANEL (Hidden on Mobile) */}
            <Box
                sx={{
                    flex: 1.2,
                    background: "linear-gradient(135deg, #642962, 0%, #4d074a 100%)",
                    display: { xs: "none", md: "flex" },
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 6,
                    color: "#fff",
                    position: "relative"
                }}
            >
                {/* Decorative subtle ambient background shapes */}
                <Box sx={{
                    position: "absolute", width: 300, height: 300, 
                    borderRadius: "50%", background: "rgba(245, 158, 11, 0.08)", 
                    top: "-5%", left: "-5%", filter: "blur(40px)"
                }} />
                
                <Box sx={{ maxWidth: 450, textAlign: "center", zIndex: 2 }}>
                    <Box 
                        component="img" 
                        src="/logo.png" 
                        onError={(e) => { e.target.style.display = 'none'; }} // fallback if no logo image yet
                        sx={{ height: 200, mb: 3, borderRadius: 2, boxShadow: 3 }} 
                        alt="Logo" 
                    />
                    <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.5px", lineHeight: 1.2, color: "#f8fafc" }}>
                        Vishwajeet Khadi Kendra
                    </Typography>
                    <Typography sx={{ mt: 2, fontSize: 16, color: "#94a3b8", fontWeight: 400, letterSpacing: "0.5px" }}>
                        Empowering Heritage & Management Portal
                    </Typography>
                </Box>
                
              
            </Box>

            {/* RIGHT SIDE: INTERACTIVE LOGIN FORM */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3
                }}
            >
                <Paper 
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: 400,
                        p: { xs: 4, sm: 5 },
                        borderRadius: 4,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.01)"
                    }}
                >
                    {/* Mobile Only Header Display */}
                    <Box sx={{ display: { xs: "block", md: "none" }, textAlign: "center", mb: 3 }}>
                        <Typography variant="h5" fontWeight="800" color="#1e293b">
                            Vishwajeet Khadi Kendra
                        </Typography>
                    </Box>

                    <Typography variant="h5" fontWeight="700" color="#1e293b" mb={1}>
                        Welcome back
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                        Please enter your admin credentials to access the console.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Email Address"
                            margin="normal"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlined fontSize="small" sx={{ color: "#94a3b8" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                mb: 2
                            }}
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            margin="normal"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined fontSize="small" sx={{ color: "#94a3b8" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                mb: 4
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.6,
                                fontWeight: "600",
                                borderRadius: 2,
                                textTransform: "none",
                                fontSize: "1rem",
                                background: "linear-gradient(90deg, #642962, #4d074a)",
                                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
                                "&:hover": {
                                    background: "#0f172a",
                                    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.2)"
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
                        </Button>
                    </Box>

                    <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 5, color: "#94a3b8" }}>
                        © {new Date().getFullYear()} Vishwajeet Khadi Kendra
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}

export default Login;